<template>
  <b-navbar class="full-nav p-0" toggleable="md" type="info" v-if="company">

    <b-navbar-brand class="m-0 my-1 mr-md-3" href="/" :title="companyName">
      <img v-if="companyLogo" :src="companyLogo" alt="Home" />
      <h2 v-else>{{ companyName }}</h2>
    </b-navbar-brand>

    <b-navbar-nav v-if="$route && `${$route.name}`.startsWith('ShareShow')">
      <b-nav-item :to="{ name: 'ShareShowAll', params: { shareCode: $route.params.shareCode } }" title="All Jobs">
        <font-awesome-icon icon="briefcase"/>
        <span class="d-inline-block d-md-none d-lg-inline-block">{{ $t('jobs.all-jobs') }}</span>
      </b-nav-item>
    </b-navbar-nav>
    <template v-else>
      <b-navbar-toggle class="border-0" target="nav_collapse"><font-awesome-icon icon="bars"/></b-navbar-toggle>

      <b-collapse is-nav id="nav_collapse">
        <b-navbar-nav class="nav-wide mt-3 mt-md-0 px-2 px-md-0" v-if="isLoggedIn">
          <b-nav-item :to="{ name: 'Dashboard' }">
            <font-awesome-icon icon="home" />
            <span class="d-inline-block d-md-none d-lg-inline-block">Home</span>
          </b-nav-item>
          <b-nav-item to="/jobs">
            <font-awesome-icon icon="briefcase" />
            <span class="d-inline-block d-md-none d-lg-inline-block">{{ $t('navigation.jobs') }}</span>
          </b-nav-item>
          <b-nav-item v-if="sitePreferences.isContentShareToolEnabled" :to="{ name: 'ContentShareIndex' }">
            <font-awesome-icon icon="share-alt" />
            <span class="d-inline-block d-md-none d-lg-inline-block">{{ $t('navigation.contentShares') }}</span>
          </b-nav-item>
          <template v-if="isRecruiterAdmin">
            <b-nav-item :to="{ name: 'ListUsers' }">
              <font-awesome-icon icon="users"/>
              <span class="d-inline-block d-md-none d-lg-inline-block">{{ $t('navigation.users') }}</span>
            </b-nav-item>
          </template>
          <template v-if="isRecruiter">
            <b-nav-item :to="{ name: 'ListReferrals' }" v-if="canSyncContacts">
              <font-awesome-icon icon="link"/>
              <span class="d-inline-block d-md-none d-lg-inline-block">{{ $t('navigation.referrals') }}</span>
            </b-nav-item>
            <b-nav-item :to="{ name: 'ListApplications' }" title="Applications">
              <font-awesome-icon icon="file-alt"/>
              <span class="d-inline-block d-md-none d-lg-inline-block">{{ $t('navigation.applications') }}</span>
            </b-nav-item>
          </template>
          <template v-else>
            <b-nav-item :to="{ name: 'Referrals' }" data-tour="your-referrals">
              <font-awesome-icon icon="user-check"/>
              <span class="d-inline-block d-md-none d-lg-inline-block">{{ $t('navigation.your-referrals') }}</span>
            </b-nav-item>
            <b-nav-item v-if="canSyncContacts" :to="{ name: 'ReferralRequests' }">
              <font-awesome-icon icon="user-plus"/>
              <span class="d-inline-block d-md-none d-lg-inline-block">{{ $t('navigation.referral-requests') }}</span>
            </b-nav-item>
          </template>

          <!-- <b-nav-item to="/candidates"><font-awesome-icon icon="user" />Candidates</b-nav-item> -->

          <template v-if="Array.isArray(leaderboards) && (isRecruiter || visibleInLeaderboards)">
            <template v-if="isRecruiter || leaderboards.length > 1">
              <b-nav-item-dropdown>
                <template v-slot:button-content>
                  <font-awesome-icon icon="list"/>
                  <span class="d-inline-block d-md-none d-lg-inline-block">{{ $t('navigation.leaderboards') }}</span>
                </template>
                <template v-for="leaderboard in leaderboards">
                  <b-dropdown-item :key="leaderboard.id" :to="{ name: 'LeaderboardShow', params: { leaderboardID: leaderboard.id }}">
                    {{leaderboard.name}}
                  </b-dropdown-item>
                </template>
                <b-dropdown-item v-if="isRecruiter" :to="{ name: 'LeaderboardIndex' }">
                  {{ $t('leaderboards.all-leaderboards') }}
                </b-dropdown-item>
              </b-nav-item-dropdown>
            </template>
            <template v-else-if="leaderboards.length === 1">
              <b-nav-item :to="{ name: 'LeaderboardShow', params: { leaderboardID: leaderboards[0].id }}">
                <font-awesome-icon icon="list" />
                <span class="d-inline-block d-md-none d-lg-inline-block">{{ $t('navigation.leaderboard') }}</span>
              </b-nav-item>
            </template>
          </template>

          <b-nav-item
            v-if="sitePreferences.isRewardsEnabled"
            :to="{ name: sitePreferences.isRewardIntegrationEnabled ? 'RewardsBrowser' : 'RewardsIndex' }">
            <font-awesome-icon icon="store" />
            <span class="d-inline-block d-md-none d-lg-inline-block">{{ $t('navigation.rewards') }}</span>
          </b-nav-item>

          <b-nav-item :to="{ name: 'ManageAnalytics' }" v-if="isRecruiter">
            <font-awesome-icon icon="chart-area" />
            <span class="d-inline-block d-md-none d-lg-inline-block">{{ $t('navigation.analytics') }}</span>
          </b-nav-item>

        </b-navbar-nav>
        <!-- <b-navbar-nav v-else>
          <b-nav-item :to="{ name: 'JobIndex' }">
            <font-awesome-icon icon="briefcase"/>
            <span class="d-inline-block d-md-none d-lg-inline-block">{{ $t('jobs.all-jobs') }}</span>
          </b-nav-item>
        </b-navbar-nav> -->

        <!-- Right aligned nav items -->
        <b-navbar-nav class="ml-auto mt-3 mt-md-0 px-2 px-md-0">
          <template v-if="user">
            <!-- <b-nav-item class="d-inline-flex align-items-top">
              <font-awesome-icon icon="bell"/>
              <span class="d-md-none">Notifications</span>
              <b-badge pill variant="danger">1</b-badge>
            </b-nav-item> -->
            <b-nav-item-dropdown right no-caret toggle-class="py-0">
              <template v-slot:button-content>
                <div class="d-inline-flex justify-content-between align-items-center pt-2" :title="user.fullName">
                  <avatar :user="user" :size="30"/>
                  <font-awesome-icon class="ml-1" icon="caret-down"/>
                </div>
              </template>

              <b-dropdown-header class="secondary-color-semi-opaque">{{ user.fullName }}</b-dropdown-header>
              <b-dropdown-header class="secondary-color-semi-opaque" v-if="user.title">{{ user.title }}</b-dropdown-header>
              <b-dropdown-item :to="{ name: 'Profile' }">{{ $t('navigation.your-profile') }}</b-dropdown-item>
              <b-dropdown-item v-if="isEnglishLang" :to="{ name: 'ProfileSessions' }">{{ $t('navigation.your-sessions') }}</b-dropdown-item>
              <b-dropdown-item v-if="isEnglishLang && !isRecruiter && canSyncContacts" :to="{ name: 'ProfileSyncContacts' }">{{ $t('navigation.sync-contacts') }}</b-dropdown-item>

              <b-dropdown-divider/>
              <b-dropdown-header class="secondary-color-semi-opaque">{{ companyName }}</b-dropdown-header>
              <b-dropdown-item v-if="company.shortCode" :to="{ name: 'AppPromo' }">{{ $t('navigation.download-app') }}</b-dropdown-item>
              <b-dropdown-item :to="{ name: 'ManageApplicationImports' }" v-if="isRecruiterAdmin">{{ $t('navigation.import-applications') }}</b-dropdown-item>
              <b-dropdown-item :to="{ name: 'ManageUserInvites' }">{{ $t('navigation.invite-people') }}</b-dropdown-item>
              <b-dropdown-item :to="{ name: 'EventList' }" v-if="isRecruiterAdmin">Referral Jams</b-dropdown-item>
              <b-dropdown-item :to="{ name: 'Settings' }" v-if="isRecruiterAdmin">{{ $t('navigation.settings') }}</b-dropdown-item>

              <template v-if="isDemoSite">
                <b-dropdown-divider/>
                <b-dropdown-text tag="div" class="px-0">
                  <demo-nav-opts/>
                </b-dropdown-text>
              </template>

              <b-dropdown-divider/>
              <b-dropdown-item v-if="$root.isAssurant" href="https://assurantconnectsgbr-my.sharepoint.com/:p:/g/personal/helen_kennedy_assurant_com/EQMxkrubpMxAt1jRiPJ8E0sBfX7JYMGCTVex5GIRkGE8OQ?e=2uCHve" target="_blank">Talent Spotters – User Guide</b-dropdown-item>
              <b-dropdown-item v-if="$root.isAssurant" href="https://assurant.reallinks.io/app" target="_blank">Talent Spotters – App</b-dropdown-item>

              <b-dropdown-item v-if="$root.isEXPD8" :to="{ name: 'eXPD8-UG' }">User Guide</b-dropdown-item>
              <b-dropdown-item v-if="$root.isEXPD8" :to="{ name: 'eXPD8-TC' }">Terms and Conditions</b-dropdown-item>

              <!-- <b-dropdown-item v-if="company && company.siteBranding && !company.siteBranding.helpURL && isRecruiter" :href="helpURLs['Recruiter-FAQs']" target="_blank">
                {{ $t(isRecruiterAdmin ? 'navigation.faqs-recruiter' : 'navigation.faqs') }}
              </b-dropdown-item> -->
              <!-- <b-dropdown-item v-if="company && company.siteBranding && !company.siteBranding.helpURL" :href="helpURLs['Employee-FAQs']" target="_blank">
                {{ $t(isRecruiter ? 'navigation.faqs-employee' : 'navigation.faqs') }}
              </b-dropdown-item> -->
              <!-- <b-dropdown-item v-if="isEnglishLang" :to="{ name: 'TrainingVideo' }">{{ $t('navigation.faqs-training-video') }}</b-dropdown-item> -->
              <b-dropdown-item href="https://support.reallinks.io/hc" v-if="isRecruiterAdmin">{{ $t('navigation.contact-support') }}</b-dropdown-item>
              <!-- <b-dropdown-item v-if="isRecruiterAdmin" :to="{ name: 'RecruiterAdminHelp' }">{{ $t('navigation.faqs-admin') }}</b-dropdown-item> -->
              <b-dropdown-item v-if="company && company.siteBranding && company.siteBranding.helpURL" :href="company.siteBranding.helpURL.fileLocation" target="_blank">{{ $t('ctas.help') }}</b-dropdown-item>
              <b-dropdown-item v-if="company && company.siteBranding && company.siteBranding.policyURL" :href="company.siteBranding.policyURL.fileLocation" target="_blank">{{ $t('navigation.policy-details') }}</b-dropdown-item>

              <b-dropdown-item v-if="user.id === 'ADMIN'" :to="{ name: 'LogoutAdmin' }">{{ $t('navigation.back-to-admin') }}</b-dropdown-item>
              <b-dropdown-item :to="{ name: 'Logout' }">{{ $t('navigation.sign-out') }}</b-dropdown-item>
            </b-nav-item-dropdown>
          </template>
        </b-navbar-nav>
      </b-collapse>
    </template>
  </b-navbar>
</template>

<script>
// import HeaderEmployeeBadges from '@/graphql/queries/HeaderEmployeeBadges.gql';
// import HeaderRecruiterBadges from '@/graphql/queries/HeaderRecruiterBadges.gql';
import Avatar from '@/components/users/Avatar';
import DemoNavOpts from '@/components/DemoNavOpts';
import helpURLs from '@/assets/helpURLs';
import { mapRoot } from '@/mixins/root';

export default {
  components: {
    Avatar,
    DemoNavOpts,
  },
  // apollo: {
  //   badges: {
  //     query() {
  //       return this.isRecruiter ? HeaderRecruiterBadges : HeaderEmployeeBadges;
  //     },
  //     update: data => data,
  //     skip() {
  //       return !this.isLoggedIn;
  //     }
  //   },
  // },
  computed: {
    ...mapRoot([ 'company', 'user', 'canSyncContacts', 'isLoggedIn', 'isRecruiter', 'isRecruiterAdmin', 'isDemoSite', 'isEnglishLang' ]),
    companyLogo() {
      const { company } = this;
      return company && company.siteBranding && company.siteBranding.bigLogo ? company.siteBranding.bigLogo.fileLocation : null;
    },
    companyName() {
      const { company } = this;
      return company && company.siteBranding && company.siteBranding.name ? company.siteBranding.name : company.name;
    },
    leaderboards() {
      if (this.isLoggedIn && this.sitePreferences.useGamification) {
        const filterActiveDates = ({ startDate, endDate }) => (!startDate || startDate < Date.now()) && (!endDate || endDate > Date.now());
        if (this.isRecruiter) {
          return (Array.isArray(this.company.leaderboards) ? this.company.leaderboards : []).filter(filterActiveDates);
        } else {
          let leaderboards = (Array.isArray(this.user.leaderboards) ? this.user.leaderboards : []).filter(filterActiveDates).filter(({ hidden }) => (!hidden));
          if (this.company.handle === 'mckesson') {
            leaderboards = leaderboards.filter(({ type }) => type === 'INDIVIDUALS');
          }
          return leaderboards;
        }
      }

      return null;
    },
    visibleInLeaderboards() {
      if (this.isLoggedIn && this.user && this.user.preferences) {
        const { visibleInLeaderboards } = this.user.preferences;
        return visibleInLeaderboards === true || visibleInLeaderboards === null;
      } else {
        return true;
      }
    },
    sitePreferences() {
      const { sitePreferences } = this.company || {};
      return sitePreferences || {};
    },
    helpURLs: () => helpURLs,
    isNewDashboard() {
      const { company } = this;
      return company && company.sitePreferences && company.sitePreferences.isNewDashboard;
    }
  },
};
</script>

<style lang="scss">
.navbar.full-nav {
  .navbar-brand {
    img {
      max-height: 30px;
      max-width: 200px;
      object-fit: contain;
    }
  }

  .navbar-nav {
    .svg-inline--fa {
      width: 16px;
    }

    .nav-item {
      font-size: 13px;
    }

    &.nav-wide {
      .svg-inline--fa {
        margin-right: 10px;
      }

      .nav-item {
        padding-right: 1rem;
      }
    }
  }
}
</style>



// WEBPACK FOOTER //
// src/components/Header.vue